import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateChallengeSimpleDetailsMutation } from '../../hooks'
import { challengePropType } from '../../prop-types'
import { setChallengePageIsDirty } from '../../redux/actions'

import { challengeSimpleDetailsFormInitialValues } from './initial-values'

const ChallengeSimpleDetailsForm = ({ challenge, maxPosition }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.CHALLENGE.UPDATE_SIMPLE_DETAILS,
  ])

  const [
    updateChallengeSimpleDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateChallengeSimpleDetailsMutation()

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const initialValues = useMemo(
    () => challengeSimpleDetailsFormInitialValues(challenge),
    [challenge],
  )

  const handleSaveData = useCallback(
    data => updateChallengeSimpleDetails(challenge.id, data),
    [challenge.id, updateChallengeSimpleDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Challenge details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [
    initialValues.synonyms,
    initialValues.isExcludedFromSearch,
    initialValues.position,
    initialValues.description,
    resetForm,
  ])

  useEffect(() => {
    dispatch(setChallengePageIsDirty({ isSimpleDetailsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editChallenge"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{challenge.name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position under category"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.CHALLENGE.UPDATE_SIMPLE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

ChallengeSimpleDetailsForm.propTypes = {
  challenge: challengePropType.isRequired,
  maxPosition: PropTypes.number.isRequired,
}

export default ChallengeSimpleDetailsForm
